// Small shared SVG icons for the redesigned owner app. Stroked, currentColor,
// 24x24 viewbox so CSS sizes them. Lifted from the design prototype.

import type { CategoryKey } from '../../lib/deviceCategory';

type IconProps = { className?: string };

const base = {
  viewBox: '0 0 24 24',
  // Intrinsic default size so an icon with no CSS size rule never balloons to
  // the 300x150 replaced-element default. CSS width/height (e.g. .rail-tab svg,
  // .sec-ico) overrides this where a specific size is wanted.
  width: 18,
  height: 18,
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false,
};

export function BrandMarkIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M12 2 3 7v6c0 5 3.5 8 9 9 5.5-1 9-4 9-9V7l-9-5Z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

export function SearchIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

export function ChevronDownIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export function ChevronRightIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

export function InstrumentsTabIcon({ className }: IconProps) {
  // A gauge: arc + needle.
  return (
    <svg {...base} className={className}>
      <path d="M4 15a8 8 0 1 1 16 0" />
      <path d="M12 15 15.5 9" />
      <path d="M4 19h16" />
    </svg>
  );
}

export function DisplaysTabIcon({ className }: IconProps) {
  // A screen on a stand (Lucide "monitor").
  return (
    <svg {...base} className={className}>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}

export function DevicesTabIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M9 9h6v6H9z" />
    </svg>
  );
}

export function AlarmsTabIcon({ className }: IconProps) {
  // A bell.
  return (
    <svg {...base} className={className}>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

export function AnalyzerIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M4 19V9M10 19V5M16 19v-7M22 19H2" />
      <path d="m4 8 6-4 6 7 5-5" />
    </svg>
  );
}

export function RefreshIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M20 7v5h-5" />
      <path d="M4 17v-5h5" />
      <path d="M6.1 9A7 7 0 0 1 18 6l2 6M18 15a7 7 0 0 1-11.9 3L4 12" />
    </svg>
  );
}

export function DownloadIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M12 3v12M7 10l5 5 5-5M4 21h16" />
    </svg>
  );
}

export function PlayIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="m7 4 13 8-13 8Z" />
    </svg>
  );
}

export function StopIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <rect x="5" y="5" width="14" height="14" rx="1" />
    </svg>
  );
}

export function SaveIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M5 3h12l2 2v16H5Z" />
      <path d="M8 3v6h8V3M8 21v-7h8v7" />
    </svg>
  );
}

export function UploadIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M12 21V9M7 14l5-5 5 5M4 3h16" />
    </svg>
  );
}

export function ManualsTabIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" />
    </svg>
  );
}

export function CamerasTabIcon({ className }: IconProps) {
  // A video camera: the boat's cameras (TOM-797).
  return (
    <svg {...base} className={className}>
      <path d="m16 13 5.2 3.5a.5.5 0 0 0 .8-.4V7.9a.5.5 0 0 0-.8-.4L16 11" />
      <rect x="2" y="6" width="14" height="12" rx="2" />
    </svg>
  );
}

export function WatchTabIcon({ className }: IconProps) {
  // An open eye: the watch officer's tab (AI Watch Assistant, ADR 0196).
  return (
    <svg {...base} className={className}>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export function PinIcon({ className }: IconProps) {
  // A push pin: the board keeps what the owner pinned (task neo_board). The
  // Board tab and every chart's Pin button share it.
  return (
    <svg {...base} className={className}>
      <path d="M8.5 3.5h7" />
      <path d="M10 3.5 9.2 9.7 6.5 13h11l-2.7-3.3L14 3.5" />
      <path d="M12 13v7.5" />
    </svg>
  );
}

export function SettingsTabIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    </svg>
  );
}

export function DocIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

export function InfoCircleIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M12 9v4M12 17h.01" />
      <circle cx="12" cy="12" r="10" />
    </svg>
  );
}

export function CloseIcon({ className }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

// --- Alarms redesign -------------------------------------------------------
// The handoff draws Phosphor icons; these are the nearest equivalents in this
// file's idiom. The three FILLED ones deliberately override the stroked `base`
// (`fill="currentColor" stroke="none"`): at 22px in a footer tile, and at 64px
// on the all-clear card, an outline reads as decoration where the design wants
// a solid state marker.

export function ListChecksIcon({ className }: IconProps) {
  // Phosphor `list-checks` — the ALL ALARMS segment.
  return (
    <svg {...base} className={className}>
      <path d="M11 6h10M11 12h10M11 18h10" />
      <path d="m3 6 1.6 1.6L7.5 4.7M3 16.5 4.6 18l2.9-2.9" />
    </svg>
  );
}

export function ClockCounterClockwiseIcon({ className }: IconProps) {
  // Phosphor `clock-counter-clockwise` — the HISTORY segment.
  return (
    <svg {...base} className={className}>
      <path d="M12 8v4l3 2" />
      <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1L3 9" />
      <path d="M3 4v5h5" />
    </svg>
  );
}

export function CheckCircleFillIcon({ className }: IconProps) {
  // Phosphor `check-circle` (fill) — all-clear, quiet, "watching" tile.
  return (
    <svg {...base} className={className} fill="currentColor" stroke="none">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm5 7.4-6.1 6.1a1 1 0 0 1-1.42 0L7 13a1 1 0 1 1 1.42-1.42l1.77 1.78 5.4-5.39A1 1 0 0 1 17 9.4Z" />
    </svg>
  );
}

export function WarningCircleFillIcon({ className }: IconProps) {
  // Phosphor `warning-circle` (fill) — the amber "N safety alarms off" tile.
  return (
    <svg {...base} className={className} fill="currentColor" stroke="none">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm-1 5a1 1 0 1 1 2 0v6a1 1 0 1 1-2 0Zm1 12a1.3 1.3 0 1 1 0-2.6 1.3 1.3 0 0 1 0 2.6Z" />
    </svg>
  );
}

export function WarningFillIcon({ className }: IconProps) {
  // Phosphor `warning` (fill) — the triangle on the ACTIVE segment.
  return (
    <svg {...base} className={className} fill="currentColor" stroke="none">
      <path d="M13.7 3.3a2 2 0 0 0-3.4 0l-8.4 14.4A2 2 0 0 0 3.6 21h16.8a2 2 0 0 0 1.7-3.3ZM11 8.5a1 1 0 1 1 2 0V14a1 1 0 1 1-2 0Zm1 10.2a1.3 1.3 0 1 1 0-2.6 1.3 1.3 0 0 1 0 2.6Z" />
    </svg>
  );
}

const CATEGORY_PATHS: Record<CategoryKey, string> = {
  navigation:
    '<circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>',
  instruments:
    '<path d="M12 14a2 2 0 1 0-2-2"/><path d="m13.4 10.6 2.6-2.6"/><path d="M3.34 19a10 10 0 1 1 17.32 0"/>',
  energy:
    '<path d="M15 7h2a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-2"/><path d="M7 17H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h2"/><path d="m11 7-3 5h4l-3 5"/><line x1="22" y1="11" x2="22" y2="13"/>',
  engine:
    '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"/>',
  safety: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/>',
  comfort:
    '<path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z"/><polyline points="9 22 9 12 15 12 15 22"/>',
};

export function CategoryIcon({
  category,
  className,
}: {
  category: CategoryKey;
  className?: string;
}) {
  return (
    <svg
      {...base}
      className={className}
      dangerouslySetInnerHTML={{ __html: CATEGORY_PATHS[category] }}
    />
  );
}
